import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Users, FileText, Home, Activity, RefreshCw, Cloud, CloudOff } from "lucide-react";
import { toast } from "sonner";
import { SupportResponseTimeAnalytics } from "@/components/SupportResponseTimeAnalytics";
import { AdminActivityFeed } from "@/components/AdminActivityFeed";
import { PlacementStatsWidget } from "@/components/PlacementStatsWidget";
import { ClaimsProcessingAnalytics } from "@/components/ClaimsProcessingAnalytics";
import { DraggableDashboard } from "@/components/admin/DraggableDashboard";
import { StatCardWidget } from "@/components/admin/widgets/StatCardWidget";
import { SupportRequestsWidget } from "@/components/admin/widgets/SupportRequestsWidget";
import { QuickActionsWidget } from "@/components/admin/widgets/QuickActionsWidget";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { WidgetErrorBoundary } from "@/components/ErrorBoundary";

interface Stats {
  totalUsers: number;
  totalClaims: number;
  pendingClaims: number;
  totalProperties: number;
  supportRequests: {
    total: number;
    open: number;
    inProgress: number;
    resolved: number;
    byPriority: { urgent: number; high: number; medium: number; low: number };
  };
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0, totalClaims: 0, pendingClaims: 0, totalProperties: 0,
    supportRequests: { total: 0, open: 0, inProgress: 0, resolved: 0, byPriority: { urgent: 0, high: 0, medium: 0, low: 0 } }
  });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const { 
    layout, 
    visibleWidgets, 
    rowDirections,
    isEditMode, 
    isLoading: layoutLoading,
    isSynced,
    canUndo,
    canRedo,
    setIsEditMode, 
    handleLayoutChange, 
    toggleRowDirection,
    addWidget, 
    removeWidget, 
    resetLayout,
    undo,
    redo,
  } = useDashboardLayout();

  const fetchSupportStats = useCallback(async () => {
    const { data: supportConversations } = await supabase.from("conversations").select("id, status, priority").eq("type", "support");
    return {
      total: supportConversations?.length || 0,
      open: supportConversations?.filter(c => c.status === "open").length || 0,
      inProgress: supportConversations?.filter(c => c.status === "in_progress").length || 0,
      resolved: supportConversations?.filter(c => c.status === "resolved").length || 0,
      byPriority: {
        urgent: supportConversations?.filter(c => c.priority === "urgent").length || 0,
        high: supportConversations?.filter(c => c.priority === "high").length || 0,
        medium: supportConversations?.filter(c => c.priority === "medium").length || 0,
        low: supportConversations?.filter(c => c.priority === "low").length || 0
      }
    };
  }, []);

  const fetchClaimsStats = useCallback(async () => {
    const { data: claims } = await supabase.from("claims").select("status");
    return { total: claims?.length || 0, pending: claims?.filter(c => c.status === "pending").length || 0 };
  }, []);

  const fetchUserCount = useCallback(async () => {
    const { count } = await supabase.from("profiles").select("*", { count: "exact", head: true });
    return count || 0;
  }, []);

  const fetchPropertyCount = useCallback(async () => {
    const { count } = await supabase.from("properties").select("*", { count: "exact", head: true });
    return count || 0;
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [userCount, claims, propertyCount, supportStats] = await Promise.all([
          fetchUserCount(), fetchClaimsStats(), fetchPropertyCount(), fetchSupportStats()
        ]);
        setStats({
          totalUsers: userCount, totalClaims: claims.total, pendingClaims: claims.pending,
          totalProperties: propertyCount, supportRequests: supportStats
        });
        setLastUpdated(new Date());
      } catch (error) { console.error("Error fetching stats:", error); }
      finally { setLoading(false); }
    };
    fetchStats();

    const channel = supabase.channel('admin-dashboard-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'conversations' }, async () => {
        const supportStats = await fetchSupportStats();
        setStats(prev => ({ ...prev, supportRequests: supportStats }));
        setLastUpdated(new Date());
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'claims' }, async () => {
        const claimsStats = await fetchClaimsStats();
        setStats(prev => ({ ...prev, totalClaims: claimsStats.total, pendingClaims: claimsStats.pending }));
        setLastUpdated(new Date());
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, async () => {
        const userCount = await fetchUserCount();
        setStats(prev => ({ ...prev, totalUsers: userCount }));
        setLastUpdated(new Date());
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'properties' }, async () => {
        const propertyCount = await fetchPropertyCount();
        setStats(prev => ({ ...prev, totalProperties: propertyCount }));
        setLastUpdated(new Date());
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchSupportStats, fetchClaimsStats, fetchUserCount, fetchPropertyCount]);

  const renderWidget = (widgetId: string) => {
    const getWidgetContent = () => {
      switch (widgetId) {
        case "stat-users":
          return <StatCardWidget title="Total Users" value={stats.totalUsers} description="Registered users" icon={Users} link="/admin/users" />;
        case "stat-claims":
          return <StatCardWidget title="Total Claims" value={stats.totalClaims} description={`${stats.pendingClaims} pending`} icon={FileText} link="/admin/claims" />;
        case "stat-properties":
          return <StatCardWidget title="Properties" value={stats.totalProperties} description="Available properties" icon={Home} link="/property-search" />;
        case "stat-status":
          return <StatCardWidget title="System Status" value="Healthy" description="All systems operational" icon={Activity} link="/admin/system" />;
        case "support-requests":
          return <SupportRequestsWidget stats={stats.supportRequests} lastUpdated={lastUpdated} />;
        case "response-time":
          return <SupportResponseTimeAnalytics />;
        case "activity-feed":
          return <AdminActivityFeed />;
        case "placement-stats":
          return <PlacementStatsWidget />;
        case "claims-processing":
          return <ClaimsProcessingAnalytics />;
        case "quick-actions":
          return <QuickActionsWidget />;
        default:
          return <Card className="h-full"><CardContent className="p-4">Widget not found</CardContent></Card>;
      }
    };

    return (
      <WidgetErrorBoundary widgetName={widgetId}>
        {getWidgetContent()}
      </WidgetErrorBoundary>
    );
  };

  if (layoutLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
            <p className="text-muted-foreground">Loading your dashboard layout...</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-64 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
          <p className="text-muted-foreground">Overview of your TPS Housing system</p>
        </div>
        <div className="flex items-center gap-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={`inline-flex items-center gap-1.5 text-sm ${isSynced ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {isSynced ? (
                    <><Cloud className="h-4 w-4" />Synced</>
                  ) : (
                    <><CloudOff className="h-4 w-4" />Local</>
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {isSynced 
                  ? "Your layout is synced across all your devices" 
                  : "Sign in to sync your layout across devices"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {lastUpdated && (
            <span className="inline-flex items-center gap-1 text-sm text-green-600">
              <RefreshCw className="h-4 w-4 animate-spin-slow" />Live
            </span>
          )}
        </div>
      </div>

      <DraggableDashboard
        layout={layout}
        visibleWidgets={visibleWidgets}
        rowDirections={rowDirections}
        isEditMode={isEditMode}
        canUndo={canUndo}
        canRedo={canRedo}
        onLayoutChange={handleLayoutChange}
        onRemoveWidget={removeWidget}
        onAddWidget={addWidget}
        onResetLayout={resetLayout}
        onToggleEditMode={() => setIsEditMode(!isEditMode)}
        onToggleRowDirection={toggleRowDirection}
        onUndo={undo}
        onRedo={redo}
        renderWidget={renderWidget}
      />
    </div>
  );
}
